import React, { useState, useMemo, useEffect } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { DisplayGroup } from '../../shared/types.js';
import Button from './Button';
import Modal from './Modal';
import { cloneDeep } from '../utils.js';
import DisplayGroupSettingsForm from './DisplayGroupSettingsForm';
import DisplayGroupMembersTab from './DisplayGroupMembersTab';

const GroupNameModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSave: (name: string) => void;
    initialName?: string;
}> = ({ isOpen, onClose, onSave, initialName = '' }) => {
    const [name, setName] = useState(initialName);
    useEffect(() => {
        if (isOpen) setName(initialName);
    }, [isOpen, initialName]);
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim()) onSave(name.trim());
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={initialName ? "Edit Group Name" : "Add Display Group"}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="group-name" className="block text-sm font-medium text-gray-600 dark:text-gray-300">Group Name</label>
                    <input
                        id="group-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required
                        className="mt-1 block w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-gray-900 dark:text-white focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary"
                        autoFocus
                    />
                </div>
                <div className="flex justify-end gap-4 pt-4">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="submit">Save</Button>
                </div>
            </form>
        </Modal>
    );
};

const DisplayGroupsSettingsTab: React.FC = () => {
    const { displayGroups, addDisplayGroup, updateDisplayGroup, deleteDisplayGroup } = useTimeClock();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [editingGroup, setEditingGroup] = useState<DisplayGroup | null>(null);
    const [isNameModalOpen, setIsNameModalOpen] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [activeInnerTab, setActiveInnerTab] = useState('Settings');
    const sortedDisplayGroups = useMemo(() => {
        return [...displayGroups].sort((a, b) => {
            if (a.isDefault) return -1;
            if (b.isDefault) return 1;
            return a.name.localeCompare(b.name);
        });
    }, [displayGroups]);
    useEffect(() => {
        if (!selectedId && sortedDisplayGroups.length > 0) {
            setSelectedId(sortedDisplayGroups[0].id);
        } else if (selectedId && !sortedDisplayGroups.some(g => g.id === selectedId)) {
            setSelectedId(sortedDisplayGroups[0]?.id || null);
        }
    }, [sortedDisplayGroups, selectedId]);
    const selectedGroup = useMemo(() => {
        return displayGroups.find(g => g.id === selectedId) || null;
    }, [selectedId, displayGroups]);
    useEffect(() => {
        if (selectedGroup) {
            setEditingGroup(cloneDeep(selectedGroup));
            setActiveInnerTab('Settings');
        } else {
            setEditingGroup(null);
        }
    }, [selectedGroup]);
    const handleApply = () => {
        if(editingGroup) {
            updateDisplayGroup(editingGroup);
        }
    };
    const handleRevert = () => {
        if(selectedGroup) {
            setEditingGroup(cloneDeep(selectedGroup));
        }
    };
    const handleSaveName = (name: string) => {
        if(isAdding) {
            addDisplayGroup(name);
        } else if (editingGroup) {
            updateDisplayGroup({...editingGroup, name});
        }
        setIsNameModalOpen(false);
    };
    const handleDelete = () => {
        if (selectedId && confirm(`Are you sure you want to delete the "${selectedGroup?.name}" group?`)) {
            deleteDisplayGroup(selectedId);
        }
    };
    const isDirty = useMemo(() => {
        if (!selectedGroup || !editingGroup) return false;
        return JSON.stringify(selectedGroup) !== JSON.stringify(editingGroup);
    }, [selectedGroup, editingGroup]);
    const INNER_TABS = ['Settings', 'Members'];
    return (
        <div className="flex bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg shadow-lg">
            <div className="w-1/4 flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <div className="flex-grow p-2 space-y-1 overflow-y-auto">
                    {sortedDisplayGroups.map(group => (
                        <button
                            key={group.id}
                            onClick={() => setSelectedId(group.id)}
                            className={`w-full text-left p-3 rounded-lg transition-colors ${selectedId === group.id ? 'bg-brand-secondary text-white font-bold' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                        >
                            {group.name}
                        </button>
                    ))}
                </div>
                <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                    <Button size="sm" onClick={() => { setIsAdding(true); setIsNameModalOpen(true); }}>Add</Button>
                    <Button size="sm" variant="danger" onClick={handleDelete} disabled={!selectedGroup || selectedGroup.isDefault}>Delete</Button>
                </div>
            </div>
            <div className="w-3/4 flex flex-col">
                {editingGroup ? (
                    <>
						 <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                            <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Group Name</label>
                            <div className="flex items-center justify-between mt-1">
                                <p className="text-2xl font-bold">{editingGroup.name}</p>
                                <Button variant="secondary" onClick={() => { setIsAdding(false); setIsNameModalOpen(true); }}>Edit</Button>
                            </div>
                        </div>
                        <div className="flex border-b border-gray-200 dark:border-gray-700">
                             {INNER_TABS.map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveInnerTab(tab)}
                                    className={`py-2 px-4 text-sm ${activeInnerTab === tab ? 'text-brand-secondary border-b-2 border-brand-secondary font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'}`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                        <div className="flex-grow p-6 overflow-y-auto">
                           {activeInnerTab === 'Settings' && editingGroup && (
                               <DisplayGroupSettingsForm
                                   group={editingGroup}
                                   onUpdate={setEditingGroup}
                               />
                           )}
                           {activeInnerTab === 'Members' && editingGroup && (
                                <DisplayGroupMembersTab
                                    group={editingGroup}
                                    onUpdate={setEditingGroup}
                                />
                           )}
                           {activeInnerTab !== 'Settings' && activeInnerTab !== 'Members' && (
                               <div className="text-center text-gray-500 py-20">
                                   {`${activeInnerTab} management is not yet implemented.`}
                               </div>
                           )}
                        </div>
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900/40 flex justify-end gap-4">
                            <Button variant="secondary" onClick={handleRevert} disabled={!isDirty}>Revert</Button>
                            <Button onClick={handleApply} disabled={!isDirty}>Apply</Button>
                        </div>
                    </>
                ) : (
                    <div className="flex items-center justify-center w-full h-full text-gray-500">
                        {displayGroups.length > 0 ? 'Select a group to see its configuration.' : 'No groups configured. Click "Add" to create one.'}
                    </div>
                )}
            </div>
             <GroupNameModal
                isOpen={isNameModalOpen}
                onClose={() => setIsNameModalOpen(false)}
                onSave={handleSaveName}
                initialName={isAdding ? '' : editingGroup?.name}
            />
        </div>
    );
};

export default DisplayGroupsSettingsTab;